import React, { useState } from 'react';
import Footer from '../components/Footer';
import { useInvoice } from '../context/InvoiceContext';

// ── Default policy content ─────────────────────────────────────────────────────

const DEFAULT_POLICY = {
  lastUpdated: 'March 5, 2026 · 12:00 PM',
  accrualNote: 'You get 12 paid days off per year. One day is added to your balance each month. Unused days are lost at year end — they do not carry over.',
  phases: [
    {
      pill: 'Probation', pillColor: 'amber',
      title: 'Months 1 – 6', duration: 'First 6 months',
      items: [
        'You get 12 days for the full year',
        'During probation you can only take up to 2 of them',
        'No booking ahead — you can only use days you have already earned',
      ],
    },
    {
      pill: 'After Probation', pillColor: 'gray',
      title: 'Months 7 – 12', duration: 'Rest of the first year',
      items: [
        'You can use all the days you have earned so far',
        'Example: if you took 2 days in probation, at month 7 you have earned 6 days total — so 4 days are left to book',
        'Still no booking ahead — only use what you have earned',
        'Needs manager approval',
      ],
    },
    {
      pill: 'Year 2+', pillColor: 'green',
      title: 'From Month 13', duration: 'From your second year onwards',
      highlight: true,
      items: [
        'You get a fresh 12 days at the start of each year',
        'You can book leave up to 6 months ahead',
        'Needs manager approval',
      ],
    },
  ],
  internRules: [
    { label: 'Paid leave',   text: 'Interns do not get paid leave.' },
    { label: 'Unpaid leave', text: 'You can request unpaid leave. All intern leave must be requested at least one week in advance — there are no unplanned days. Needs manager approval and is at your own expense.' },
    { label: 'How to apply', text: 'Go to the Calendar page and choose the unpaid leave option.' },
    { label: 'Note',         text: 'Long absences may affect your internship.' },
  ],
  generalRules: [
    { label: 'Notice period',   text: 'All leave must be requested at least one week before the start date. This applies to both paid and unpaid leave, for employees and interns.' },
    { label: 'Unplanned leave', text: 'Regular employees only — up to 3 of your 12 days can be taken without advance notice, for emergencies or unexpected situations. Interns must give notice for all leave.' },
    { label: 'Approval',        text: 'All leave requests need manager approval before they are confirmed.' },
    { label: 'Conflicts',       text: 'If you already have a pending or approved request for those dates, a new request will not be accepted.' },
    { label: 'Cancellation',    text: 'To cancel approved leave, speak to your manager directly — you cannot cancel through the app.' },
  ],
};

const POLICY_VERSION = 8;

const loadPolicy = () => {
  try {
    const saved = localStorage.getItem('leavePolicy');
    const version = Number(localStorage.getItem('leavePolicyVersion') || 0);
    if (saved && version >= POLICY_VERSION) return JSON.parse(saved);
  } catch {}
  return DEFAULT_POLICY;
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const Pill = ({ color, children }) => {
  const styles = {
    green: { bg: 'rgba(22,163,74,0.08)',  color: '#15803d', border: 'rgba(22,163,74,0.25)'  },
    amber: { bg: 'rgba(217,119,6,0.08)',  color: '#b45309', border: 'rgba(217,119,6,0.3)'   },
    red:   { bg: 'rgba(220,38,38,0.07)',  color: '#dc2626', border: 'rgba(220,38,38,0.22)'  },
    gray:  { bg: 'rgba(0,0,0,0.04)',      color: '#5E5A56', border: 'rgba(0,0,0,0.10)'      },
  }[color] || {};
  return (
    <span style={{
      display: 'inline-block', fontSize: '11px', fontWeight: 600,
      padding: '2px 9px', borderRadius: '20px',
      background: styles.bg, color: styles.color,
      border: `1px solid ${styles.border}`,
    }}>{children}</span>
  );
};

const SectionCard = ({ title, badge, children }) => (
  <div className="card" style={{ marginBottom: 'var(--spacing-lg)', padding: 0, overflow: 'hidden' }}>
    <div style={{
      padding: '14px 20px',
      borderBottom: '1px solid var(--color-gray-100)',
      display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--color-text-primary)' }}>{title}</span>
      {badge && <Pill color={badge.color}>{badge.label}</Pill>}
    </div>
    <div style={{ padding: '20px' }}>{children}</div>
  </div>
);

// ── Read-only view ─────────────────────────────────────────────────────────────

const PhaseCard = ({ phase }) => (
  <div style={{
    flex: 1,
    border: '1px solid rgba(185,28,28,0.25)',
    borderTop: '3px solid var(--color-primary)',
    borderRadius: '4px',
    padding: '20px',
    background: 'var(--color-surface)',
  }}>
    <div style={{ marginBottom: '12px' }}><Pill color={phase.pillColor}>{phase.pill}</Pill></div>
    <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '2px' }}>{phase.title}</div>
    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>{phase.duration}</div>
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {phase.items.map((item, i) => (
        <li key={i} style={{ display: 'flex', gap: '8px', fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: '1.5' }}>
          <span style={{ color: 'var(--color-gray-300)', marginTop: '1px', flexShrink: 0 }}>–</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const RuleRow = ({ label, children }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: '160px 1fr', gap: '16px',
    padding: '14px 0', borderBottom: '1px solid var(--color-gray-100)',
    fontSize: '14px', alignItems: 'baseline',
  }}>
    <div style={{ fontWeight: 600, color: 'var(--color-text-primary)', fontSize: '13px' }}>{label}</div>
    <div style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>{children}</div>
  </div>
);

// ── Edit mode components ───────────────────────────────────────────────────────

const inputBase = {
  width: '100%', boxSizing: 'border-box',
  border: '1px solid var(--color-gray-200)',
  borderRadius: '4px',
  padding: '6px 10px',
  fontSize: '13px',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  fontFamily: 'inherit',
  outline: 'none',
};

const EditPhaseCard = ({ phase, idx, onChange }) => {
  const set = (field, val) => onChange(idx, { ...phase, [field]: val });
  const setItem = (i, val) => {
    const items = [...phase.items];
    items[i] = val;
    onChange(idx, { ...phase, items });
  };
  const addItem = () => onChange(idx, { ...phase, items: [...phase.items, ''] });
  const removeItem = (i) => {
    const items = phase.items.filter((_, j) => j !== i);
    onChange(idx, { ...phase, items });
  };

  return (
    <div style={{
      flex: 1,
      border: `1px solid ${phase.highlight ? 'rgba(185,28,28,0.25)' : 'var(--color-gray-200)'}`,
      borderTop: `3px solid ${phase.highlight ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
      borderRadius: '4px',
      padding: '16px',
      background: 'var(--color-surface)',
      display: 'flex', flexDirection: 'column', gap: '8px',
    }}>
      <input style={inputBase} value={phase.pill} onChange={e => set('pill', e.target.value)} placeholder="Label" />
      <input style={inputBase} value={phase.title} onChange={e => set('title', e.target.value)} placeholder="Title" />
      <input style={{ ...inputBase, fontSize: '12px' }} value={phase.duration} onChange={e => set('duration', e.target.value)} placeholder="Duration" />
      <div style={{ marginTop: '4px' }}>
        {phase.items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
            <input
              style={{ ...inputBase, flex: 1 }}
              value={item}
              onChange={e => setItem(i, e.target.value)}
              placeholder={`Item ${i + 1}`}
            />
            <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: '16px', padding: '0 4px', lineHeight: 1 }}>×</button>
          </div>
        ))}
        <button onClick={addItem} style={{ fontSize: '12px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add item</button>
      </div>
    </div>
  );
};

const EditRuleRow = ({ rule, onChange, onRemove }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: '10px',
    padding: '10px 0', borderBottom: '1px solid var(--color-gray-100)', alignItems: 'start',
  }}>
    <input style={inputBase} value={rule.label} onChange={e => onChange({ ...rule, label: e.target.value })} placeholder="Label" />
    <textarea
      style={{ ...inputBase, resize: 'vertical', minHeight: '52px' }}
      value={rule.text}
      onChange={e => onChange({ ...rule, text: e.target.value })}
      placeholder="Rule description"
    />
    <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: '18px', padding: '6px 4px', lineHeight: 1 }}>×</button>
  </div>
);

// ── Main page ──────────────────────────────────────────────────────────────────

const Policies = () => {
  const { currentUser } = useInvoice();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [policy, setPolicy] = useState(loadPolicy);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]   = useState(null);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(policy))); setEditing(true); };
  const cancelEdit = () => { setDraft(null); setEditing(false); };
  const saveEdit = () => {
    const now = new Date();
    const updated = { ...draft, lastUpdated: now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) + ' · ' + now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) };
    setPolicy(updated);
    localStorage.setItem('leavePolicy', JSON.stringify(updated));
    localStorage.setItem('leavePolicyVersion', String(POLICY_VERSION));
    setDraft(null);
    setEditing(false);
  };

  const d = editing ? draft : policy;

  const setPhase = (idx, val) => setDraft(p => { const phases = [...p.phases]; phases[idx] = val; return { ...p, phases }; });
  const setInternRule = (idx, val) => setDraft(p => { const internRules = [...p.internRules]; internRules[idx] = val; return { ...p, internRules }; });
  const addInternRule = () => setDraft(p => ({ ...p, internRules: [...p.internRules, { label: '', text: '' }] }));
  const removeInternRule = (idx) => setDraft(p => ({ ...p, internRules: p.internRules.filter((_, i) => i !== idx) }));
  const setGeneralRule = (idx, val) => setDraft(p => { const generalRules = [...p.generalRules]; generalRules[idx] = val; return { ...p, generalRules }; });
  const addGeneralRule = () => setDraft(p => ({ ...p, generalRules: [...p.generalRules, { label: '', text: '' }] }));
  const removeGeneralRule = (idx) => setDraft(p => ({ ...p, generalRules: p.generalRules.filter((_, i) => i !== idx) }));

  return (
    <>
      <main className="container" style={{ flex: 1, padding: 'var(--spacing-lg) var(--spacing-md)', width: '100%', boxSizing: 'border-box' }}>

        {/* Page header */}
        <div className="flex-between mb-xl" style={{ flexWrap: 'wrap', gap: 'var(--spacing-md)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: '4px' }}>Leave Policy</h1>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
              Leave entitlements and rules for full-time employees and interns.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Updated {policy.lastUpdated}</span>
            {isSuperAdmin && !editing && (
              <button onClick={startEdit} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </button>
            )}
            {isSuperAdmin && editing && (
              <>
                <button onClick={cancelEdit} className="btn btn-secondary btn-sm">Cancel</button>
                <button onClick={saveEdit} className="btn btn-primary btn-sm">Save</button>
              </>
            )}
          </div>
        </div>

        {/* Edit mode banner */}
        {editing && (
          <div style={{
            marginBottom: 'var(--spacing-lg)',
            padding: '10px 16px',
            background: 'rgba(37,99,235,0.06)',
            border: '1px solid rgba(37,99,235,0.2)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: '#1d4ed8',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Editing mode — click Save when done.
          </div>
        )}

        {/* Full-time employees */}
        <SectionCard title="Full-Time Employees" badge={{ label: '12 days / year', color: 'green' }}>
          {editing ? (
            <textarea
              className="form-input"
              style={{ minHeight: '60px', resize: 'vertical', marginBottom: 'var(--spacing-md)', width: '100%', boxSizing: 'border-box' }}
              value={draft.accrualNote}
              onChange={e => setDraft(p => ({ ...p, accrualNote: e.target.value }))}
            />
          ) : (
            <p style={{ margin: '0 0 var(--spacing-lg)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.7 }}>
              {d.accrualNote}
            </p>
          )}

          <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: editing ? 'wrap' : 'nowrap' }}>
            {d.phases.map((phase, idx) =>
              editing
                ? <EditPhaseCard key={idx} phase={phase} idx={idx} onChange={setPhase} />
                : <PhaseCard key={idx} phase={phase} />
            )}
          </div>
        </SectionCard>

        {/* Interns */}
        <SectionCard title="Interns" badge={{ label: 'No paid leave', color: 'red' }}>
          {d.internRules.map((rule, idx) =>
            editing ? (
              <EditRuleRow
                key={idx} rule={rule}
                onChange={val => setInternRule(idx, val)}
                onRemove={() => removeInternRule(idx)}
              />
            ) : (
              <RuleRow key={idx} label={rule.label}>
                {idx === 0
                  ? <><Pill color="red">Not applicable</Pill><span style={{ marginLeft: 6 }}>{rule.text}</span></>
                  : idx === d.internRules.length - 1
                    ? <span style={{ fontStyle: 'italic' }}>{rule.text}</span>
                    : rule.text
                }
              </RuleRow>
            )
          )}
          {editing && (
            <button onClick={addInternRule} style={{ marginTop: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              + Add rule
            </button>
          )}
        </SectionCard>

        {/* General rules */}
        <SectionCard title="General Rules">
          {d.generalRules.map((rule, idx) =>
            editing ? (
              <EditRuleRow
                key={idx} rule={rule}
                onChange={val => setGeneralRule(idx, val)}
                onRemove={() => removeGeneralRule(idx)}
              />
            ) : (
              <RuleRow key={idx} label={rule.label}>{rule.text}</RuleRow>
            )
          )}
          {editing && (
            <button onClick={addGeneralRule} style={{ marginTop: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              + Add rule
            </button>
          )}
        </SectionCard>

      </main>
      <Footer />
    </>
  );
};

export default Policies;
